import { useState } from "react";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>("textarea[name='message']");
    if (messageField && messageField.value.length > 500) {
      return;
    }
    setSubmitStatus("submitting");
    const formData = new URLSearchParams();
    formData.append("_to", "c.green.support@gmail.com");
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name && input.value) {
        formData.append(input.name, input.value);
      }
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d74enlv5hic0eqh314c0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  const handleClose = () => {
    setSubmitStatus("idle");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D7A3E] to-[#3D9A52] px-8 py-6">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-mail-send-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">お問い合わせ</h2>
              <p className="text-white/80 text-sm">技術・導入に関するご相談はこちら</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          {submitStatus === "success" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-[#E8F5E9] rounded-full mx-auto mb-4">
                <i className="ri-check-line text-[#2D7A3E] text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-2">送信が完了しました</h3>
              <p className="text-sm text-gray-600 mb-6">
                お問い合わせいただきありがとうございます。<br />
                担当者より2〜3営業日以内にご連絡いたします。
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-[#2D7A3E] text-white rounded-lg font-semibold text-sm hover:bg-[#245A30] transition-colors cursor-pointer whitespace-nowrap"
              >
                閉じる
              </button>
            </div>
          ) : submitStatus === "error" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-2">送信に失敗しました</h3>
              <p className="text-sm text-gray-600 mb-6">
                通信エラーが発生しました。<br />
                しばらく経ってから再度お試しください。
              </p>
              <button
                onClick={() => setSubmitStatus("idle")}
                className="px-6 py-2.5 bg-[#2D7A3E] text-white rounded-lg font-semibold text-sm hover:bg-[#245A30] transition-colors cursor-pointer whitespace-nowrap"
              >
                もう一度入力する
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A2332] mb-1.5">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="山田 太郎"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2D7A3E] focus:ring-1 focus:ring-[#2D7A3E] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2332] mb-1.5">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="example@company.co.jp"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2D7A3E] focus:ring-1 focus:ring-[#2D7A3E] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2332] mb-1.5">
                  所属（会社・自治体名）
                </label>
                <input
                  type="text"
                  name="organization"
                  placeholder="〇〇市役所 / 株式会社〇〇"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2D7A3E] focus:ring-1 focus:ring-[#2D7A3E] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2332] mb-1.5">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <select
                  name="inquiry_type"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2D7A3E] focus:ring-1 focus:ring-[#2D7A3E] transition-colors bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="技術資料の請求">技術資料の請求</option>
                  <option value="施設見学の申込">施設見学の申込</option>
                  <option value="導入・連携のご相談">導入・連携のご相談</option>
                  <option value="その他">その他</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2332] mb-1.5">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="ご質問・ご要望をご記入ください（500文字以内）"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2D7A3E] focus:ring-1 focus:ring-[#2D7A3E] transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitStatus === "submitting"}
                className="w-full py-3 bg-[#2D7A3E] text-white font-semibold rounded-lg hover:bg-[#245A30] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {submitStatus === "submitting" ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </span>
                ) : (
                  "送信する"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
